import type { ReactNode } from "react";
import { ActionIcon, Badge, Button, Group, Stack, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import logo from "./assets/peeko-logo.svg";
import type { AccountSummary, Role } from "./auth/session";
import { signOut } from "./auth/session";
import styles from "./Panel.module.scss";

/**
 * The panel's frame: who is signed in, the way out, and whatever the router put inside it.
 *
 * Every route inside it passes the same staff gate, server-side (`App.tsx` says where), so the frame
 * itself carries no permission check.
 *
 * The running-a-show furniture §17.4 asks for — stream health, viewer count, the comment feed — is
 * not here. It belongs to one show, so it lives on the show page (`shows/ShowPage.tsx`) rather than
 * in a frame that also wraps the show list.
 */
export function Panel({
  account,
  roles,
  onSignedOut,
  children,
}: {
  account: AccountSummary;
  roles: Role[];
  onSignedOut: () => Promise<void>;
  children: ReactNode;
}) {
  const { t } = useTranslation();

  return (
    <Stack gap="lg">
      {/* The same logo the sign-in screen leads with, smaller, so signing in does not feel like
          arriving at a different product. */}
      <Group
        justify="space-between"
        align="center"
        wrap="nowrap"
        className={styles.header}
      >
        {/*
          `min-width: 0` lets this side shrink on a phone, so a long name or email ends in an ellipsis
          rather than pushing the buttons past the edge of the screen.
        */}
        <Group gap="md" align="center" wrap="nowrap" className={styles.who}>
          {/* The logo is the way back to the show list — the one navigation this panel has. */}
          <Link to="/" aria-label={t("panel.heading")}>
            <img className={styles.logo} src={logo} alt={t("app.logoAlt")} />
          </Link>
          <Stack gap={2} className={styles.who}>
            <Text fw={650} size="sm" c="ink.8" truncate="end">
              {account.displayName ?? account.email}
            </Text>
            <Group gap={6}>
              {roles.map((role) => (
                <Badge key={role} variant="light" color="ink" size="sm" radius="sm">
                  {role}
                </Badge>
              ))}
            </Group>
          </Stack>
        </Group>
        <Group gap="xs" wrap="nowrap">
          {/* The catalogue and the platform's settings (M1.13) — the host's; a moderator has no
              use for either and the routes behind the page refuse them. */}
          {/*
            The word on a wide screen, a cog on a phone — where the word and *Sign out* beside the
            account do not fit in one row. The cog carries the word as its accessible name.
          */}
          {roles.includes("host") ? (
            <>
              <Button variant="subtle" component={Link} to="/settings" visibleFrom="xs">
                {t("panel.settings")}
              </Button>
              <ActionIcon
                variant="subtle"
                size="lg"
                component={Link}
                to="/settings"
                hiddenFrom="xs"
                aria-label={t("panel.settings")}
              >
                <CogIcon />
              </ActionIcon>
            </>
          ) : null}
          <Button variant="default" onClick={() => void signOut().then(onSignedOut)}>
            {t("panel.signOut")}
          </Button>
        </Group>
      </Group>

      {children}
    </Stack>
  );
}

/** A settings cog, drawn inline: the panel has no icon library and needs only this one. */
function CogIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
